/**
 * StepTutorial — 动画演示教程播放器（无需真实视频）。
 * 自动播放（每 6 秒一步）+ 播放/暂停 + 上一步/下一步 + 进度条 + 步骤跳转，
 * 每步右侧「去这里操作」直接跳到系统对应页面。
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, ChevronLeft, ChevronRight, ArrowRight, RotateCcw, PlayCircle } from "lucide-react";

const STEP_MS = 6000;

export default function StepTutorial({ script, lang = "zh" }) {
  const zh = lang === "zh";
  const navigate = useNavigate();
  const steps = script.steps;
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setTimeout(() => {
      setIdx((i) => (i + 1 < steps.length ? i + 1 : (setPlaying(false), i)));
    }, STEP_MS);
    return () => clearTimeout(timer.current);
  }, [playing, idx, steps.length]);

  const step = steps[idx];

  if (!started) {
    return (
      <button
        type="button"
        onClick={() => { setStarted(true); setPlaying(true); }}
        className="mt-4 w-full flex items-center gap-3 rounded-md border-2 border-dashed border-brand/40 bg-brand/5 px-4 py-3 text-left hover:bg-brand/10 transition-colors"
        data-testid={`tutorial-start-${script.id}`}
      >
        <PlayCircle size={22} className="text-brand shrink-0" />
        <span className="min-w-0">
          <span className="block text-sm font-medium truncate">{script.title}</span>
          <span className="block text-xs text-muted-foreground">
            {zh ? `${steps.length} 步 · 自动播放，也可以自己翻页` : `${steps.length} steps · plays automatically, or step through yourself`}
          </span>
        </span>
        <span className="ml-auto text-xs font-semibold text-brand shrink-0">{zh ? "开始演示" : "Start"}</span>
      </button>
    );
  }

  return (
    <section className="mt-4 rounded-md border border-brand/30 bg-white overflow-hidden"
      data-testid={`tutorial-player-${script.id}`}>
      {/* header + controls */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 bg-brand/5">
        <span className="font-mono text-[11px] font-bold text-brand">
          {String(idx + 1).padStart(2, "0")}/{String(steps.length).padStart(2, "0")}
        </span>
        <span className="text-sm font-medium truncate">{script.title}</span>
        <span className="ml-auto flex items-center gap-1">
          <IconBtn onClick={() => { setPlaying(false); setIdx((i) => Math.max(0, i - 1)); }}
            disabled={idx === 0} testId={`tutorial-prev-${script.id}`}><ChevronLeft size={14} /></IconBtn>
          <IconBtn onClick={() => setPlaying((p) => !p)} testId={`tutorial-play-${script.id}`}>
            {playing ? <Pause size={14} /> : <Play size={14} />}
          </IconBtn>
          <IconBtn onClick={() => { setPlaying(false); setIdx((i) => Math.min(steps.length - 1, i + 1)); }}
            disabled={idx === steps.length - 1} testId={`tutorial-next-${script.id}`}><ChevronRight size={14} /></IconBtn>
          <IconBtn onClick={() => { setIdx(0); setPlaying(true); }} testId={`tutorial-restart-${script.id}`}>
            <RotateCcw size={13} />
          </IconBtn>
        </span>
      </div>

      {/* progress dots */}
      <div className="flex gap-1 px-3 pt-2">
        {steps.map((s, i) => (
          <button key={s.title} type="button" title={s.title}
            onClick={() => { setPlaying(false); setIdx(i); }}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= idx ? "bg-brand" : "bg-border"}`}
            data-testid={`tutorial-dot-${script.id}-${i}`} />
        ))}
      </div>

      {/* body */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4 p-4" key={idx}>
        <div className="animate-fade-in-up">
          <h4 className="font-medium">{step.title}</h4>
          <p className="mt-1 text-sm leading-relaxed text-foreground/90">{step.desc}</p>
          <div className="mt-2 flex items-start gap-1.5 text-xs bg-amber-50 border-l-2 border-amber-400 pl-2 py-1.5 rounded-r">
            <span className="font-mono font-semibold text-amber-700 shrink-0">TIP</span>
            <span className="text-amber-900 leading-relaxed">{step.hint}</span>
          </div>
          <button type="button" onClick={() => navigate(step.route || script.route)}
            className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-brand text-white text-xs font-semibold hover:bg-brand-hover transition-colors"
            data-testid={`tutorial-goto-${script.id}`}>
            {zh ? "去这里操作" : "Try it now"} <ArrowRight size={12} />
          </button>
        </div>
        <Screen screen={step.screen} />
      </div>
    </section>
  );
}

const IconBtn = ({ onClick, disabled, children, testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className="h-7 w-7 inline-flex items-center justify-center rounded border border-border bg-white text-muted-foreground hover:text-brand hover:border-brand/60 disabled:opacity-40 transition-colors">
    {children}
  </button>
);

/** 声明式界面示意图 — 让每一步都有画面感，而不是纯文字。 */
function Screen({ screen }) {
  if (!screen) return null;
  const box = "rounded-md border border-border bg-secondary/25 p-2.5 animate-fade-in-up";

  if (screen.kind === "form") {
    return (
      <div className={box} data-testid="tutorial-screen">
        {screen.title && <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{screen.title}</p>}
        <div className="space-y-1.5">
          {screen.items.map((it) => (
            <div key={it.k} className={`flex items-center justify-between gap-2 rounded px-2 py-1 text-xs ${it.hit ? "bg-brand/10 ring-1 ring-brand/40" : "bg-white"}`}>
              <span className="text-muted-foreground">{it.k}</span>
              <span className="font-mono font-medium">{it.v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (screen.kind === "table") {
    return (
      <div className={box} data-testid="tutorial-screen">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-muted-foreground">
              {screen.head.map((h, i) => <th key={`h${i}`} className="pb-1 font-normal">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {screen.rows.map((r, ri) => (
              <tr key={`r${ri}`} className={ri === screen.hit ? "bg-brand/10" : "bg-white"}>
                {r.map((c, ci) => <td key={`c${ci}`} className="px-1 py-1 font-mono truncate">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (screen.kind === "buttons") {
    return (
      <div className={box} data-testid="tutorial-screen">
        <div className="flex flex-wrap gap-1.5">
          {screen.items.map((b, i) => (
            <span key={b} className={`h-7 px-2.5 inline-flex items-center rounded text-[11px] font-medium ${i === 0 ? "bg-brand text-white" : "bg-white border border-border text-muted-foreground"}`}>
              {b}
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (screen.kind === "banner") {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 animate-fade-in-up" data-testid="tutorial-screen">
        <p className="text-[11px] leading-relaxed text-emerald-800 font-mono">{screen.text}</p>
      </div>
    );
  }
  if (screen.kind === "cards") {
    return (
      <div className={box} data-testid="tutorial-screen">
        {screen.title && <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{screen.title}</p>}
        <div className="grid grid-cols-3 gap-1.5">
          {screen.items.map((c, i) => (
            <div key={c} className={`h-12 rounded flex items-center justify-center text-[10px] font-mono ${i === 0 ? "bg-brand text-white" : "bg-white border border-border text-muted-foreground"}`}>
              {c}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}
